import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, Validators } from '@angular/forms';
import { ITokenValid } from '@pacifico/interfaces/components.interface';
import { IRequestClientDoubleFactor } from '@pacifico/interfaces/shared-api.interface';
import { SharedApiService } from '@pacifico/services/api/shared/shared-api.service';
import { TokenService } from '@pacifico/services/api/token.service';
import { AlertService } from '@pacifico/services/local/alert.service';
import { SHIPPING_WAY } from '@pacifico/utils/enums';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

@Component({
	selector: 'app-valid-token-phone',
	templateUrl: './valid-token-phone.component.html',
	styleUrls: ['./valid-token-phone.component.scss']
})
export class ValidTokenPhoneComponent implements OnInit, OnChanges, OnDestroy {
	@Output() token = new EventEmitter<ITokenValid>();
	/**
	 **Cuando es coordenada se cumple, el tokenType = 2
	 *Cuando es sms o email se cumple que el el tokenType = 1
	 */
	@Output() tokenType = new EventEmitter<string>();
	@Input() operationCode: string | undefined;
	@Input() overrideEmail: string | undefined;
	@Input() overridePhone: string | undefined;
	@Input() clear = false;
	@Input() showAlternativeMethod = true;
	@Input() overrideShippingWay = false;

	formArray: FormArray;
	private subscription!: Subscription;
	shippingWay: string;
	email: string;
	phone: string;
	mask_email = '';
	mask_phone = '';
	phone_new_value = '';

	constructor(
		private _formBuilder: FormBuilder,
		private _sharedApiService: SharedApiService,
		private _tokenService: TokenService,
		private _alertService: AlertService
	) {
		this.shippingWay = this._tokenService.getProfileAccountUser().preferences;
		this.email = this.overrideEmail ? this.overrideEmail : this._tokenService.getProfileAccountUser().email;
		this.phone = this.overridePhone ? this.overridePhone : `${this._tokenService.getProfileAccountUser().mobile}`;

		this.formArray = this._formBuilder.array([]);
		for (let index = 0; index < 4; index++) {
			this.formArray.insert(index, new FormControl('', Validators.required));
		}
		this.subscriptionValue();
	}

	ngOnChanges(): void {
		this.email = this.overrideEmail ? this.overrideEmail : this._tokenService.getProfileAccountUser().email;
		this.phone = this.overridePhone ? this.overridePhone : `${this._tokenService.getProfileAccountUser().mobile}`;
		this.shippingWay = this.overrideShippingWay ? '1' : this.shippingWay;

		if (this.clear) {
			this.formArray.reset();
		}
	}

	ngOnInit(): void {
		this._requestDefaultDoubleFactor();
		this.mask_phone = this.maskCharacter(this.phone, '*', 3);
		const characterEmail = this.email.substr(this.email.indexOf('@'), this.email.length - 1);
		this.mask_email = this.maskCharacter(this.email, '*', characterEmail.length, 2);
	}

	cardCoordinate = this._tokenService.getProfileAccountUser().cardCoordinate;
	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	keyDown(control: AbstractControl, event: KeyboardEvent): void {
		const element = event.target as HTMLInputElement;

		if (event.key === 'Backspace') {
			this._nextPreviuElement(control, element, false);
		} else {
			const rex = new RegExp('^[0-9]*$');
			if (rex.test(event.key) === false) {
				return;
			}
			this._nextPreviuElement(control, element, true);
		}
	}

	clickValidate(): void {
		this._validate();
	}

	clickResend(): void {
		this._requestDefaultDoubleFactor();
	}

	private _requestDefaultDoubleFactor(): void {
		const requestClient: IRequestClientDoubleFactor = {
			operationCode: this._getOperationCode(),
			personId: this._tokenService.getUser(),
			shippingDetails: this.overrideShippingWay
				? this.phone
				: this._tokenService.getShippingDetailUserPhone(this.phone), //se valida a donde se envia el token
			shippingWay: this.shippingWay
		};

		this._sendToken(requestClient);
	}

	private subscriptionValue(): void {
		this.subscription = this.formArray.valueChanges.pipe(debounceTime(500)).subscribe((data) => {
			if (data) {
				const item = `${this.formArray.at(0).value as string} ${this.formArray.at(1).value as string} ${
					this.formArray.at(2).value as string
				} ${this.formArray.at(3).value as string}`;

				const tokenValid = item.replace(/\s/g, '');
				this.token.emit({ token: tokenValid, valid: this.formArray.valid });
			}
		});
	}

	private _nextPreviuElement(control: AbstractControl, element: HTMLInputElement, isNext: boolean): void {
		const value = control.value as string;
		const elementSibling = isNext ? element.nextElementSibling : element.previousElementSibling;
		const elementInput = elementSibling as HTMLInputElement;

		if (value) {
			control.setValue('');
		}

		if (elementInput == null) {
			return;
		}

		if (!isNext) {
			control.reset();
		}

		setTimeout(() => {
			elementInput.focus();
		}, 250);
	}

	private _validate(): void {
		const requestClient: Partial<IRequestClientDoubleFactor> = {
			operationCode: this._getOperationCode(),
			personId: this._tokenService.getUser()
		};
		// si el shippingWay actual es mobile o es coordenada entonces quiere decir que se enviara el token al email
		if (this.shippingWay === SHIPPING_WAY.MOBILE || this.shippingWay === SHIPPING_WAY.COORDINATE) {
			requestClient.shippingDetails = this.email;
			requestClient.shippingWay = SHIPPING_WAY.EMAIL;
		} else {
			requestClient.shippingDetails = `${this.phone}`;
			requestClient.shippingWay = SHIPPING_WAY.MOBILE;
		}
		this._sendToken(requestClient as IRequestClientDoubleFactor);
	}

	private _getOperationCode(): string {
		let operationCode = this.operationCode;
		if (!operationCode) {
			operationCode = this._tokenService.getProfileAccountUser().operationCode;
		}
		return operationCode;
	}

	maskCharacter(text: string, mask: string, showCount = 1, showCountInit?: number): string {
		if (showCountInit) {
			return (
				('' + text).slice(0, showCountInit) +
				('' + text).slice(showCountInit, -showCount).replace(/./g, mask) +
				('' + text).slice(-showCount)
			);
		}

		return ('' + text).slice(0, -showCount).replace(/./g, mask) + ('' + text).slice(-showCount);
	}

	private _sendToken(requestClient: IRequestClientDoubleFactor): void {
		this._sharedApiService.clientDoubleFactor(requestClient).subscribe((response) => {
			if (response) {
				if (response.status.code === '0000' && response.data.code !== '0000') {
					this._alertService.showError(response.data.message);
					return;
				}
				if (response.status.code === '0000' && response.data.code === '0000') {
					this.tokenType.emit(
						this.overrideShippingWay ? requestClient.shippingWay : this._getTokenType(requestClient.shippingWay)
					);
				}

				if (
					response.status.code === '0000' &&
					response.data.code === '0000' &&
					this.shippingWay === SHIPPING_WAY.COORDINATE &&
					response.data.coordinates
				) {
					this.cardCoordinate = response.data.coordinates;
				}
			} else {
				this._alertService.showError('Ocurrio un error inesperado, vuelve a intentar');
			}
		});
	}

	private _getTokenType(shippingWay: string): string {
		return shippingWay;
		if (shippingWay === SHIPPING_WAY.COORDINATE) {
			return '2';
		}

		return '1';
	}

	validateShowSendPhone(): boolean {
		// dont show if shippingway is phone
		return this.shippingWay === '1' || this.shippingWay === '3';
	}
}
